import React from "react"
import { Navigate } from "react-router-dom"
// Authentication related pages
import Login from "../pages/Authentication/Login"
import Logout from "../pages/Authentication/Logout"
import Register from "../pages/Authentication/Register"
import ForgetPwd from "../pages/Authentication/ForgetPassword"

// Dashboard
import Dashboard from "../pages/Dashboard/index"
import RegisterWizard from "pages/Authentication/RegistrationWizard"
import PartsUpload from "pages/PartsUpload"
import PartSearch from "pages/PartSearch"
import PartSearchDetails from "pages/PartSearchDetails"
import Settings from "pages/Settings"
import Pages404 from "pages/Util/pages-404"
import Users from "pages/Users"
import ConfirmMail from "pages/Authentication/ConfirmEmail"
import ConfirmMailChange from "pages/Authentication/ConfirmEmailChange"
import ResetPassword from "pages/Authentication/ChangePassword"
import Requests from "pages/Requests"
import SavedParts from "pages/SavedParts"
import Watchlist from "pages/Watchlist"
import WatchlistUpload from "pages/WatchlistUpload"
import Pages403 from "pages/Util/pages-403"
import AccountPendingConfirmation from "pages/Util/AccountPendingConfirmation"
import TrialExpired from "pages/Util/TrialExpired"
import { IsPremiumUser, IsCpsWarehouse } from "helpers/misc_helpers"
import QCWizard from "pages/QC/QCRequestWizard"
import QCDetails from "pages/QC/QCDetails"
import QCRequests from "pages/QC/QCRequests"
import PartnerCockpit from "pages/PartnerCockpit"
import PartnerCockpitDetails from "pages/PartnerCockpit/PartnerCockpitDetails"
import PartnerCockpitDemandList from "pages/PartnerCockpit/DemandList"
import PartnerCockpitPartList from "pages/PartnerCockpit/PartList"
import LOAUpload from "pages/PartnerCockpit/LOAUpload"
import LOADetails from "pages/PartnerCockpit/LOADetails"
import DemandDetails from "pages/PartnerCockpit/DemandDetails"
import RepairWizard from "pages/Repair/RepairRequestWizard"
import RepairDetails from "pages/Repair/RepairDetails"
import RepairRequests from "pages/Repair/RepairRequests"
import CPSWizard from "pages/CPS/CPSRequestWizard"
import CPSDetails from "pages/CPS/CPSDetails"
import CPSRequests from "pages/CPS/CPSRequests"
import MyMaterialStockList from "pages/PartnerCockpit/MyMaterialStock"
import PartnerCockpitInvitation from "pages/PartnerCockpit/PartnerCockpitInvitation"
import LOAList from "pages/PartnerCockpit/LOAList"
import PublicPartSearch from "pages/PublicPartSearch"
import TraderRequests from "pages/TraderRequests"
import NewTraderRequest from "pages/TraderRequests/NewTraderRequest"
import TraderRequestDetails from "pages/TraderRequests/TraderRequestDetails"
import { IsTraderCompany } from "helpers/misc_helpers"

const authProtectedRoutes = [
  { path: "/dashboard", component: <Dashboard /> },
  { path: "/parts-upload", component: <PartsUpload /> },
  { path: "/part-search", component: <PartSearch /> },
  { path: "/part-search/:partID", component: <PartSearchDetails /> },
  {
    path: "/users",
    component: <Users />,
    checkUser: user => user.userType != "Admin",
  },
  { path: "/settings", component: <Settings /> },
  {
    path: "/requests",
    component: <Requests />,
    checkUser: user => !IsPremiumUser(),
  },
  { path: "/saved-parts", component: <SavedParts /> },
  { path: "/watchlist", component: <Watchlist /> },
  { path: "/qc-requests", component: <QCRequests /> },
  { path: "/qc-request-wizard", component: <QCWizard /> },
  { path: "/qc-requests/:id", component: <QCDetails /> },
  { path: "/repair-requests", component: <RepairRequests /> },
  { path: "/repair-requests/wizard", component: <RepairWizard /> },
  { path: "/repair-requests/:id", component: <RepairDetails /> },
  { path: "/cps-requests", component: <CPSRequests /> },
  {
    path: "/cps-request-wizard",
    component: <CPSWizard />,
    // Warehouse users are not allowed to create new CPS requests
    checkUser: user => IsCpsWarehouse(),
  },
  { path: "/cps-requests/:id", component: <CPSDetails /> },

  { path: "/trader-requests", component: <TraderRequests />, checkUser: user => !IsPremiumUser() },
  {
    path: "/trader-requests/new",
    component: <NewTraderRequest />,
    // Trader dürfen keine neuen Requests erstellen
    checkUser: user => IsTraderCompany(),
  },
  { path: "/trader-requests/:id", component: <TraderRequestDetails /> },

  { path: "/watchlist-upload", component: <WatchlistUpload /> },
  { path: "/partner-cockpit", component: <PartnerCockpit /> },
  { path: "/partner-cockpit/:id", component: <PartnerCockpitDetails /> },
  {
    path: "/partner-cockpit/demand-list",
    component: <PartnerCockpitDemandList />,
  },
  { path: "/partner-cockpit/part-list", component: <PartnerCockpitPartList /> },
  { path: "/partner-cockpit/loa-upload", component: <LOAUpload /> },
  { path: "/partner-cockpit/loa-list", component: <LOAList /> },
  { path: "/partner-cockpit/loa-details/:id", component: <LOADetails /> },
  { path: "/partner-cockpit/demand-details/:id", component: <DemandDetails /> },
  {
    path: "/partner-cockpit/my-material-stock",
    component: <MyMaterialStockList />,
  },
  {
    path: "/partner-cockpit-invitation/:id",
    component: <PartnerCockpitInvitation />,
  },

  {
    path: "/",
    exact: true,
    component: <Navigate to="/dashboard" />,
  },
]

const publicRoutes = [
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <Login /> },
  { path: "/forgot-password", component: <ForgetPwd /> },
  { path: "/register", component: <Register /> },
  { path: "/email-confirmation", component: <ConfirmMail /> },
  { path: "/change-email-confirmation", component: <ConfirmMailChange /> },
  { path: "/register-wizard", component: <RegisterWizard /> },
  { path: "/reset-password", component: <ResetPassword /> },
  { path: "/403", component: <Pages403 /> },
  { path: "/trial-expired", component: <TrialExpired /> },
  {
    path: "/public-part-search",
    component: <PublicPartSearch />,
    hideLogo: true,
  },
  {
    path: "/account-confirmation-pending",
    component: <AccountPendingConfirmation />,
  },
  { path: "*", component: <Pages404 /> },
]

export { authProtectedRoutes, publicRoutes }
